import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import {
  Wallet, ShieldAlert, CheckCircle2, Send, Lock, FileWarning,
  Inbox, BadgeCheck, UserX, RefreshCw, AlertTriangle,
} from "lucide-react";

// Phase HR-4A — Owner Dashboard HR Payroll Control Tile.
//
// Reuse summary:
//   • Reuses the existing `co-card` Owner Dashboard tile style.
//   • Reuses `api` + `useAuth` (with `canHR`) helpers.
//   • Calls the new `GET /api/hr/dashboard/payroll-control-summary` endpoint.
//   • Counts-only — never displays salary totals, net pay, gross,
//     payment, WPS, bank, finance, GL, or any employee-level salary detail.

const GROUPS = [
  {
    title: "Needs Action",
    rows: [
      { key: "runs_awaiting_validation",  label: "Awaiting validation",
        tone: "amber", icon: ShieldAlert,
        to: "/hr/payroll-runs?status=awaiting_validation" },
      { key: "runs_with_blocked_employees", label: "Blocked employees",
        tone: "rose",  icon: UserX,
        to: "/hr/payroll-runs?status=blocked" },
      { key: "runs_ready_for_calculation", label: "Ready for calculation",
        tone: "blue",  icon: CheckCircle2,
        to: "/hr/payroll-runs?status=ready_for_calculation" },
    ],
  },
  {
    title: "Approval Queue",
    rows: [
      { key: "runs_calculated_awaiting_submission", label: "Calculated · awaiting submission",
        tone: "amber", icon: Inbox,
        to: "/hr/payroll-runs?status=calculated" },
      { key: "runs_submitted_for_approval", label: "Submitted for approval",
        tone: "blue",  icon: Send,
        to: "/hr/payroll-runs?status=submitted" },
      { key: "runs_approved_awaiting_lock", label: "Approved · awaiting lock",
        tone: "amber", icon: Lock,
        to: "/hr/payroll-runs?status=approved" },
    ],
  },
  {
    title: "Payslip Release",
    rows: [
      { key: "locked_runs_without_payslips", label: "Locked · payslips missing",
        tone: "rose",  icon: FileWarning,
        to: "/hr/payroll-runs?status=locked_without_payslips" },
      { key: "generated_payslips_awaiting_release", label: "Generated · awaiting release",
        tone: "amber", icon: Inbox,
        // HR-4A.1 — no dedicated /hr/payslips list page exists; we
        // route through the payroll-runs page filtered by payslip
        // release status. The PayrollRuns page (HR-4A.1) honours this.
        to: "/hr/payroll-runs?release_status=generated" },
      { key: "released_payslips_this_month", label: "Released this month",
        tone: "emerald", icon: BadgeCheck,
        to: "/hr/payroll-runs?release_status=released" },
    ],
  },
  {
    title: "Employee Linking",
    rows: [
      { key: "employees_without_linked_user", label: "Employees without linked user",
        tone: "amber", icon: UserX,
        to: "/hr/employees?linked_user=missing" },
    ],
  },
];

const TONE = {
  rose:    { count: "text-rose-700",    bg: "bg-rose-50    border-rose-200",    icon: "text-rose-600" },
  amber:   { count: "text-amber-800",   bg: "bg-amber-50   border-amber-200",   icon: "text-amber-700" },
  blue:    { count: "text-sky-800",     bg: "bg-sky-50     border-sky-200",     icon: "text-sky-700" },
  emerald: { count: "text-emerald-800", bg: "bg-emerald-50 border-emerald-200", icon: "text-emerald-700" },
};


export default function HrPayrollControlCard() {
  const { canHR } = useAuth();
  const sub = useSubscription();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Phase HR-DASH-ENTITLEMENT (2026-07-08) — this widget must NOT render
  // (and must NOT fetch) for tenants without the HR module. Enforced by
  // combining the user-permission gate with the tenant-entitlement gate.
  //
  // Loading semantics: `sub.hasModule("hr")` defaults to `true` while
  // entitlements are still loading (legacy compatibility). We gate on
  // `entitlementLoaded` so the API call waits until the subscription
  // context has actual data — never firing a request that would be
  // rejected with MODULE_NOT_SUBSCRIBED for unsubscribed tenants.
  const allowedByPermission = canHR("hr.dashboard.read");
  const entitlementLoaded = sub && !sub.loading;
  const hasHrModule = entitlementLoaded && sub.hasModule("hr");
  const allowed = allowedByPermission && hasHrModule;

  const load = async () => {
    if (!allowed) return;
    setLoading(true); setError(null);
    try {
      const r = await api.get("/hr/dashboard/payroll-control-summary");
      setData(r.data?.data || null);
    } catch (e) {
      setError(formatErr(e?.response?.data) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [allowed]);

  // Hide the widget cleanly when either gate fails. Return null once we
  // know the answer — during the brief initial subscription load we
  // also return null (no flicker, no premature fetch).
  if (!allowedByPermission) return null;
  if (!entitlementLoaded) return null;
  if (!hasHrModule) return null;

  return (
    <div className="co-card p-slate p-5" data-testid="hr-payroll-control-card">
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900 inline-flex items-center gap-2">
            <Wallet className="h-5 w-5 text-cyan-700" />
            HR Payroll Control
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Payroll lifecycle alerts · {data?.period_key || "current month"}
          </p>
        </div>
        <button type="button"
                onClick={load} disabled={loading}
                className="text-slate-400 hover:text-slate-700 p-1.5 rounded"
                title="Refresh"
                data-testid="hr-payroll-control-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </header>

      {loading && !data ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : data ? (
        <Content data={data} />
      ) : (
        <Empty />
      )}
    </div>
  );
}


function Content({ data }) {
  const totalAlerts = GROUPS
    .flatMap((g) => g.rows)
    .reduce((s, r) => s + (Number(data[r.key]) > 0 && r.tone !== "emerald" ? 1 : 0), 0);
  // HR-4A.1 — append the active period to the "Released this month"
  // link so PayrollRuns honours the period filter end-to-end.
  const decoratedGroups = GROUPS.map((g) => ({
    ...g,
    rows: g.rows.map((r) => (
      r.key === "released_payslips_this_month" && data.period_key
        ? { ...r, to: `${r.to}&period_key=${data.period_key}` }
        : r
    )),
  }));
  return (
    <div className="space-y-4" data-testid="hr-payroll-control-content">
      {totalAlerts === 0 ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-900 inline-flex items-center gap-2"
             data-testid="hr-payroll-control-all-clear">
          <CheckCircle2 className="h-4 w-4" />
          All payroll lifecycle queues are clear.
        </div>
      ) : null}

      {decoratedGroups.length > 0 ? (
        /*
          2026-02-27 · Phase Owner-Dash-HR-Width
          Group sections previously stacked vertically inside a
          `space-y-4` flex column. On the Owner Dashboard this card now
          spans the full row, so we lay out the four sections as a
          responsive grid:
            mobile (default) → 1 column
            tablet  (sm)      → 2 columns
            desktop (xl)      → 4 columns
          Items stretch so the section borders stay aligned at the bottom.
        */
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-4 items-stretch"
             data-testid="hr-payroll-control-grid">
          {decoratedGroups.map((g) => (
            <section key={g.title} className="flex flex-col"
                     data-testid={`hr-payroll-control-group-${g.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
                {g.title}
              </div>
              <div className="space-y-1 flex-1">
                {g.rows.map((r) => (
                  <MetricRow key={r.key} row={r} value={Number(data[r.key] || 0)} />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : null}

      <div className="text-[10px] text-slate-400 pt-1 border-t border-slate-100">
        Counts only · no salary, payment, WPS, or finance data. Updated {fmtTime(data.last_updated_at)}.
      </div>
    </div>
  );
}


function MetricRow({ row, value }) {
  const tone = TONE[row.tone] || TONE.blue;
  const Icon = row.icon;
  const isZero = value === 0;
  const inner = (
    <div className={`flex items-center justify-between gap-3 px-2 py-1.5 rounded-md ${isZero ? "" : "hover:bg-slate-50"}`}>
      <div className="flex items-center gap-2 min-w-0">
        <Icon className={`h-3.5 w-3.5 ${isZero ? "text-slate-300" : tone.icon}`} />
        <span className={`text-xs truncate ${isZero ? "text-slate-400" : "text-slate-700"}`}>
          {row.label}
        </span>
      </div>
      <span className={`text-base font-extrabold tabular-nums ${isZero ? "text-slate-300" : tone.count}`}
            data-testid={`hr-payroll-control-${row.key}`}>
        {value}
      </span>
    </div>
  );
  if (isZero || !row.to) return inner;
  return (
    <Link to={row.to}
          className="block"
          data-testid={`hr-payroll-control-link-${row.key}`}>
      {inner}
    </Link>
  );
}


function Loading() {
  return (
    <div className="space-y-2" data-testid="hr-payroll-control-loading">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-7 bg-slate-100 rounded animate-pulse" />
      ))}
    </div>
  );
}


function Empty() {
  return (
    <div className="text-xs text-slate-500 py-3" data-testid="hr-payroll-control-empty">
      No payroll activity in this period yet.
    </div>
  );
}


function ErrorState({ message, onRetry }) {
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50/70 px-3 py-2 text-xs text-rose-800"
         data-testid="hr-payroll-control-error">
      <div className="inline-flex items-center gap-1.5 font-semibold">
        <AlertTriangle className="h-3.5 w-3.5" /> Could not load payroll summary
      </div>
      <div className="mt-1 opacity-90">{message}</div>
      <button type="button" onClick={onRetry}
              className="mt-1.5 text-rose-900 underline text-[11px]"
              data-testid="hr-payroll-control-retry">Retry</button>
    </div>
  );
}


function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined,
      { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
